import { useEffect, useState } from 'react';
import { LinkedNotice } from './LinkedNotice';

const NUMERIC = ['money', 'number', 'percent'];
const inputCls = 'w-full rounded-xl border bg-white px-4 py-3 text-sm text-matte focus:outline-none focus:border-gold transition-colors duration-200';

const toNumber = (raw) => {
  if (raw.trim() === '') return { value: null };
  const n = Number(raw.replace(/[$,%\s]/g, ''));
  if (Number.isNaN(n) || n < 0) return { error: 'Enter a number of 0 or more' };
  if (n > 1e9) return { error: 'That number is too large' };
  return { value: n };
};

export const FieldInput = ({ field, value, onChange, onBlur, missing, serverError, link, onLinkAction, personalize, primary }) => {
  const numeric = NUMERIC.includes(field.type);
  const [raw, setRaw] = useState(numeric ? (value ?? '').toString() : '');
  const [localError, setLocalError] = useState('');
  useEffect(() => {
    if (numeric && toNumber(raw).value !== (value ?? null)) setRaw((value ?? '').toString());
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const id = `fbp-${field.key}`;
  const err = localError || serverError;
  const border = missing || err ? 'border-red-400' : 'border-matte/15';
  const selected = field.type === 'select' ? field.options.find((o) => o.value === value) : null;

  const changeNumber = (v) => {
    setRaw(v);
    const r = toNumber(v);
    setLocalError(r.error || '');
    if (!r.error) onChange(r.value);
  };

  return (
    <div className="py-6 border-t border-matte/10 first:border-t-0" data-testid={`field-${field.key}`}>
      <label htmlFor={id} className="block text-[15px] text-matte font-medium leading-snug">
        {personalize(field.label)} {field.required && <span className="text-gold" aria-label="required">*</span>}
      </label>
      {field.help && <p className="mt-1.5 text-sm text-matte/60 leading-relaxed">{personalize(field.help)}</p>}
      {link && <LinkedNotice link={link} onAction={onLinkAction} />}
      <div className="mt-3">
        {field.type === 'textarea' && (
          <textarea id={id} rows={3} maxLength={3000} value={value ?? ''} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}
            className={`${inputCls} ${border} resize-y`} data-testid={`input-${field.key}`} aria-invalid={!!(missing || err)} />
        )}
        {field.type === 'text' && (
          <input id={id} maxLength={3000} value={value ?? ''} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} placeholder={field.placeholder}
            className={`${inputCls} ${border}`} data-testid={`input-${field.key}`} aria-invalid={!!(missing || err)} />
        )}
        {numeric && (
          <div className="relative max-w-xs">
            {field.unit === '$' && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-matte/50 text-sm">$</span>}
            <input id={id} inputMode="decimal" value={raw} onChange={(e) => changeNumber(e.target.value)} onBlur={onBlur}
              className={`${inputCls} ${border} ${field.unit === '$' ? 'pl-8' : ''} ${field.unit === '%' ? 'pr-8' : ''}`}
              data-testid={`input-${field.key}`} aria-invalid={!!(missing || err)} />
            {field.unit === '%' && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-matte/50 text-sm">%</span>}
          </div>
        )}
        {field.type === 'multiselect' && (
          <div className="flex flex-wrap gap-2" role="group" aria-labelledby={id}>
            {field.options.filter((o) => o.value !== primary).map((o) => {
              const on = (value || []).includes(o.value);
              return (
                <button key={o.value} type="button" role="checkbox" aria-checked={on}
                  onClick={() => { onChange(on ? value.filter((v) => v !== o.value) : [...(value || []), o.value]); onBlur(); }}
                  className={`rounded-full border px-4 py-2 text-sm transition-colors duration-200 ${on ? 'border-gold bg-gold/10 text-matte' : 'border-matte/15 bg-white text-matte/70 hover:border-matte/30'}`}
                  data-testid={`option-${field.key}-${o.value}`}>
                  {o.label}
                </button>
              );
            })}
          </div>
        )}
        {field.type === 'select' && (
          <div className="grid gap-2" role="radiogroup" aria-labelledby={id}>
            {field.options.map((o) => (
              <button key={o.value} type="button" role="radio" aria-checked={value === o.value}
                onClick={() => { onChange(o.value); onBlur(); }}
                className={`text-left rounded-xl border px-4 py-3 text-sm transition-colors duration-200 ${value === o.value ? 'border-gold bg-gold/10 text-matte' : `${border} bg-white text-matte/80 hover:border-matte/30`}`}
                data-testid={`option-${field.key}-${o.value}`}>
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {selected?.feedback && (
        <p className="mt-3 rounded-xl bg-ivory border border-gold/30 px-4 py-3 text-sm text-matte/80 leading-relaxed" data-testid={`feedback-${field.key}`}>
          <span className="text-[10px] uppercase tracking-[0.2em] text-gold block mb-1">Guidance</span>{selected.feedback}
        </p>
      )}
      {field.examples?.length > 0 && (
        <details className="mt-3 text-sm text-matte/60">
          <summary className="cursor-pointer text-[11px] uppercase tracking-[0.18em] text-matte/50" data-testid={`examples-${field.key}`}>See examples</summary>
          <ul className="mt-2 space-y-1 list-disc pl-5">{field.examples.map((x) => <li key={x}>{x}</li>)}</ul>
        </details>
      )}
      {(missing || err) && <p className="mt-2 text-sm text-red-700" data-testid={`error-${field.key}`}>{err || 'This answer is required to complete the stage.'}</p>}
    </div>
  );
};
